import type { GroupOptions } from 'sortablejs';
import Sortable, { type SortableEvent } from 'sortablejs';

import { assert } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { supportsTouch } from '@disy/lib/media';
import { px } from '@disy/lib/px';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';

import { RemoveOnSpillPlugin } from 'cadenza/workbook/sortable-list/remove-on-spill-plugin';
import { ReplaceAttributePlugin } from 'cadenza/workbook/sortable-list/replace-attribute-plugin';
import type { View } from 'cadenza/workbook/view/view';
import type { EmptyView } from 'cadenza/workbook/view/view-empty/empty-view';
import { COMPONENT_NAME as EMPTY_VIEW_ELEMENT_NAME } from 'cadenza/workbook/view/view-empty/empty-view';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import './sortable-list.css';

const COMPONENT_NAME = 'd-sortable-list';
const DRAG_CLASS = `${COMPONENT_NAME}--drag`;
const CHOSEN_CLASS = `${COMPONENT_NAME}--chosen`;
const GHOST_CLASS = `${COMPONENT_NAME}--item-ghost`;
const HOVER_CLASS = 'hover-while-dragging';
const DROP_DENIED_CLASS = 'dropping-denied';
const IS_SOURCE_CLASS = 'is-source';
const IS_TARGET_CLASS = 'is-target';
const COPY_MODE_CLASS = 'copy-mode';

Sortable.mount(RemoveOnSpillPlugin);
Sortable.mount(ReplaceAttributePlugin);

export class SortableList extends HTMLElement {
  #sortable;
  #dragOverEventKey?: Teardown;
  draggableSelector;
  #preventDragOnAlt;
  #teardowns: Teardown[] = [];

  /**
   * Constructs a sortable list of draggable items.
   * Warning: The drag & drop capabilities will we disabled for touchable devices
   *
   * @param options - provides additional options how to interact with other components.
   * @param options.handleDropAreaActivation - Check whether should sortable activate drop area.
   * Used when drop area is not an instance of Sortable (but handled natively by dragover and drop events);
   * by adding extra class ('drop-active') enables to turn on special drop area handling, required for
   * instance when iFrame exists within drop area
   * @param options.group - The group attribute
   * @param options.pull - The pull attribute
   * @param options.push - The push attribute
   * @param options.draggableSelector - Indicates which children are draggable
   * @param options.handleSelector - Indicates on which element dragging can be started
   * @param options.disabled - Indicates if the element is disabled or not
   * @param options.draggableInTouch - Indicates if the element is draggable or not in touch devices
   * @param options.touchHandleSelector - Indicates on which element dragging can be started on touch devices
   * @param options.enableReplace - If true the replace plugin will be enabled on the sortable
   * @param options.preserveHeight - If true the height of this element will be preserved while dragging occurs.
   * @param options.forceDisplayGhost - In some cases, displaying the drag ghost after leaving the drop area is preferred UX-wise.
   * @param options.preventDragOnAlt - Holding alt key (or opt on mac) will stop the dragging and allow user to select text.
   */
  constructor({
    handleDropAreaActivation = false,
    group,
    pull,
    push,
    draggableSelector = 'li',
    handleSelector,
    disabled = false,
    enableReplace = false,
    draggableInTouch = false,
    touchHandleSelector,
    preserveHeight = false,
    forceDisplayGhost = false,
    preventDragOnAlt = false,
  }: {
    handleDropAreaActivation?: boolean;
    group?: string;
    pull?: string | false;
    push?: string | boolean;
    draggableSelector?: string;
    handleSelector?: string;
    disabled?: boolean;
    enableReplace?: boolean;
    draggableInTouch?: boolean;
    touchHandleSelector?: string;
    preserveHeight?: boolean;
    forceDisplayGhost?: boolean;
    preventDragOnAlt?: boolean;
  } = {}) {
    super();
    let possibleTargetLists: SortableList[] = [];
    let excludedTargetLists: SortableList[] = [];
    this.draggableSelector = draggableSelector;
    this.#preventDragOnAlt = preventDragOnAlt;
    if (preventDragOnAlt) {
      draggableSelector = `:not(.${COMPONENT_NAME}--prevent-drag) > ${draggableSelector}`;
    }
    const sortableOptions: Sortable.Options & { disyReplaceAttribute: boolean; disyRemoveOnSpill: boolean } = {
      disabled,
      // this isn't doing everything we want, see https://github.com/SortableJS/Sortable/issues/1703,
      // but it is an improvement, as we can get rid of at least _some_ undesired animations.
      animation: 150,

      // recommended for nested lists, see https://sortablejs.github.io/sortablejs/#nested
      fallbackOnBody: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 0,
      [ReplaceAttributePlugin.pluginName]: enableReplace,

      chosenClass: CHOSEN_CLASS,
      dragClass: DRAG_CLASS,
      ghostClass: GHOST_CLASS,
      draggable: draggableSelector,
      handle: this._getEffectiveHandleSelector(draggableInTouch, handleSelector, touchHandleSelector),
      dragoverBubble: true,
      onChoose:
        preserveHeight ?
          () => {
            this.style.height = px(this.clientHeight);
          }
        : undefined,
      onUnchoose:
        preserveHeight ?
          () => {
            this.style.removeProperty('height');
          }
        : undefined,
      onStart: (event) => {
        const onStartCallback = this.getOnStartProvider();
        if (onStartCallback) {
          onStartCallback(event);
        }

        const fromGroup = (event.from as SortableList).group;
        const allTargetLists = document.querySelectorAll<SortableList>(COMPONENT_NAME);

        // set pointer events to none for list with different group
        excludedTargetLists = [...allTargetLists].filter((list) => fromGroup !== (list as SortableList).group);
        for (const list of excludedTargetLists) {
          list.style.pointerEvents = 'none';
        }

        // data browser may contain many sortable lists which cannot be targets, so we filter them out
        possibleTargetLists = [...allTargetLists].filter((list) => !list.closest(WorkbookPanelName.DATA_BROWSER));
        event.from.classList.add(IS_SOURCE_CLASS);
        if (this.pull === 'copy') {
          event.from.classList.add(COPY_MODE_CLASS);
        }
        event.to.classList.add(IS_TARGET_CLASS);

        /*
        Dragleave, dragenter occur frequently and also when hovering over children elements.
        We use dragover to make sure we are outside of the sortable-list and in that case we hide
        droparea and clear is-target class.
         */
        this.#dragOverEventKey = on(
          window,
          'dragover',
          throttle((e) => {
            // Due to throttling it can happen that the drag over handler is called after the drag interaction already stopped.
            // So we do the check here and don't do anything in case the drag over event handler was already disconnected.
            if (!this.#dragOverEventKey) {
              return;
            }
            const closestList = document.elementFromPoint(e.clientX, e.clientY)!.closest(COMPONENT_NAME);
            possibleTargetLists
              .filter((sortable) => sortable !== closestList)
              .forEach((sortable) => {
                if (!forceDisplayGhost) {
                  sortable.classList.remove(IS_TARGET_CLASS);
                }
                sortable._unmarkDropArea();
              });

            if (handleDropAreaActivation) {
              this._activateDropArea();
              if (closestList && this.ariaLabelledBy === closestList.getAttribute('aria-labelledby')) {
                this.classList.add(IS_SOURCE_CLASS, IS_TARGET_CLASS);
              }
            }
            this.dispatchEvent(new CustomEvent('sortstart'));
          }, 30),
        );
      },
      onMove: (event) => {
        const to = event.to as SortableList;
        const from = event.from;
        const parentSortableList = to.parentElement?.closest(`.${COMPONENT_NAME}`);

        if (
          parentSortableList &&
          [...parentSortableList.children].some((item) => item.classList.contains(GHOST_CLASS))
        ) {
          to._unmarkDropArea();
          to.classList.remove(IS_SOURCE_CLASS, IS_TARGET_CLASS);
          return;
        }

        /*
        The only time we highlight droparea is when we drag item over empty list. In this case, the
        droparea is highlighted and ghost is not shown. In case of non-empty list the ghost is visible.
         */
        if (to.classList.contains('empty')) {
          to._markDropArea();
        }
        from.classList.remove(IS_SOURCE_CLASS, IS_TARGET_CLASS);
        to.classList.remove(IS_SOURCE_CLASS, IS_TARGET_CLASS);
        from.classList.add(IS_SOURCE_CLASS);
        to.classList.add(IS_TARGET_CLASS);
      },
      onEnd: (event) => {
        this.#dragOverEventKey = tearDown(this.#dragOverEventKey);
        const onEndCallback = this.getOnEndProvider();

        if (!onEndCallback) {
          const originalEvent = (event as SortableEvent & { originalEvent: DragEvent }).originalEvent;
          const { clientX, clientY } = originalEvent;
          if (clientX !== undefined && clientY !== undefined) {
            // safeguard for touch event
            const elementUnderPoint = document.elementFromPoint(clientX, clientY);
            const closestView = elementUnderPoint?.closest<View | EmptyView>(
              `d-worksheet-view, ${EMPTY_VIEW_ELEMENT_NAME}`,
            );
            closestView?.handleAttributeDrop(originalEvent);
          }
        }

        // in case user drops item on denied droparea we need to unmark that area onEnd
        possibleTargetLists.forEach((sortable) => {
          sortable.classList.remove(IS_TARGET_CLASS);
          sortable._unmarkDropArea();
        });
        this.checkDropArea();
        if (handleDropAreaActivation) {
          this._deactivateDropArea();
        }
        event.from.classList.remove(IS_SOURCE_CLASS, IS_TARGET_CLASS, COPY_MODE_CLASS);
        event.to.classList.remove(IS_SOURCE_CLASS, IS_TARGET_CLASS, DROP_DENIED_CLASS);
        (event.to as SortableList)._unmarkDropArea();

        // restore pointer events
        for (const list of excludedTargetLists) {
          list.style.pointerEvents = 'auto';
        }

        this.dispatchEvent(new CustomEvent('sortend', { detail: event }));

        if (onEndCallback) {
          onEndCallback(event);
        }
      },
      disyRemoveOnSpill: true,
    };
    this.#sortable = Sortable.create(this, sortableOptions);

    // We block change events to be able as they also get fired by Sortable when an event is
    // "visually inserted" into another list in dragover events. If not blocked some listeners
    // waiting for actual change events on other input elements (under the same delegate) will be
    // called assuming something has changed. We used thad for catching all changes in a component
    // without knowing its actual input elements.
    // Anyway for noticing changes to the list we do have separate events from Sortable (e.g. 'add'
    // and 'remove').
    on(this, 'change', (event) => event.stopPropagation());

    if (group) {
      this.setAttribute('group', group);
    }
    if (pull) {
      this.setAttribute('pull', pull);
    }
    if (push) {
      this.setAttribute('push', String(push));
    }
    this.toggleAttribute('disabled', disabled);

    this._applyOptions();
  }

  connectedCallback() {
    this.setAttribute('role', 'list');
    this.classList.add(COMPONENT_NAME);
    this.checkDropArea();
    if (this.#preventDragOnAlt) {
      this.#setupAltKeyListener();
    }
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  #setupAltKeyListener() {
    this.#teardowns.push(on(document, ['keydown', 'keyup'], this.#updateAltKeyStatus.bind(this), { passive: true }));
  }

  #updateAltKeyStatus(e: KeyboardEvent) {
    this.classList.toggle(`${COMPONENT_NAME}--prevent-drag`, e.altKey);
  }

  isActive() {
    return Sortable.active === this.#sortable;
  }

  checkDropArea() {
    const isEmpty = !this.querySelector(`${this.draggableSelector}:not(.${GHOST_CLASS})`);
    this.classList.toggle('empty', isEmpty);
  }

  _markDropArea() {
    this.classList.add(HOVER_CLASS);
  }

  _unmarkDropArea() {
    this.classList.remove(HOVER_CLASS);
    this.classList.remove(DROP_DENIED_CLASS);
  }

  _activateDropArea() {
    document.querySelectorAll('.drop-area').forEach((dropArea) => dropArea.classList.add('drop-active'));
  }

  _deactivateDropArea() {
    document.querySelectorAll('.drop-area').forEach((dropArea) => dropArea.classList.remove('drop-active'));
  }

  /**
   * Calculates the effective DraggableSelector based on some logic:
   * For non touch device: draggableSelector
   * For a touch device: if draggableInTouch false (false by default) 'no-element', the touchDraggableSelector otherwise
   *
   * @param draggableInTouch - Indicates if the list should be sortable also for touch devices
   * @param handleSelector - The selector for the handle in non touch devices
   * @param touchHandleSelector - The selector for the handle in touch devices
   * @return The effective handle selector to be applied
   */
  _getEffectiveHandleSelector(draggableInTouch: boolean, handleSelector?: string, touchHandleSelector?: string) {
    if (!supportsTouch) {
      return handleSelector;
    }
    if (!draggableInTouch) {
      return 'no-element'; // disables the handler for touch devices
    }
    return touchHandleSelector;
  }

  get dropDenied() {
    return this.classList.contains(DROP_DENIED_CLASS);
  }

  set dropDenied(value) {
    this.classList.toggle(DROP_DENIED_CLASS, value);
  }

  get disabled() {
    return this.#sortable.options.disabled === true;
  }

  set disabled(value) {
    this.#sortable.options.disabled = value;
    this.toggleAttribute('disabled', value);
  }

  set ariaLabelledBy(value: string) {
    this.setAttribute('aria-labelledby', value);
  }

  get ariaLabelledBy(): string | null {
    return this.getAttribute('aria-labelledby');
  }

  set group(value) {
    if (value) {
      this.setAttribute('group', value);
    } else {
      this.removeAttribute('group');
    }
    this._applyOptions();
  }

  get group() {
    return this.getAttribute('group');
  }

  set push(value) {
    this.toggleAttribute('push', value);
    this._applyOptions();
  }

  get push() {
    return this.hasAttribute('push');
  }

  set pull(value) {
    if (value) {
      assert(['move', 'copy'].includes(value), '"pull" value must be either "move" or "copy"', value);
      this.setAttribute('pull', value);
    } else {
      this.removeAttribute('pull');
    }
    this._applyOptions();
  }

  get pull() {
    return this.getAttribute('pull');
  }

  _pullInternal() {
    // Sortable.js uses 'clone' instead of 'copy' and requires `true` instead of 'move', so let's convert:
    return this.pull === 'copy' ? 'clone' : true;
  }

  _applyOptions() {
    this.#sortable.option('group', this._groupOptions);
    // for now, if you can add things, you can also sort them there
    this.#sortable.option('sort', Boolean(this.push));
  }

  get _groupOptions() {
    if (!this.group) {
      return;
    }
    return {
      name: this.group,
      put:
        this.push &&
        ((to, from, item, dragEvent) => {
          const pushEvent = new CustomEvent('push', {
            cancelable: true,
            detail: { item, from, dragEvent },
          });
          return this.dispatchEvent(pushEvent);
        }),
      pull:
        this.pull &&
        ((from, to, item) => {
          const pullEvent = new CustomEvent('pull', {
            cancelable: true,
            detail: { item },
          });
          if (this.dispatchEvent(pullEvent)) {
            return this._pullInternal();
          }
        }),
    } as GroupOptions;
  }

  /**
   * Returns a callback with additional logic that in special cases needs to be executed onStart,
   * e.g. when rendering the sortable list using lit-html.
   */
  protected getOnStartProvider(): ((event: SortableEvent) => void) | void {
    /* To be overridden by subclass */
  }

  /**
   * Returns a callback with additional logic that in special cases needs to be executed onEnd,
   * e.g. when rendering the sortable list using lit-html.
   */
  protected getOnEndProvider(): ((event: SortableEvent) => void) | void {
    /* To be overridden by subclass */
  }
}

customElements.define(COMPONENT_NAME, SortableList);
